import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import Section from "../sections/Section";
import Element from "../elements/Element";
import sortElements from "../elements/sortElements.js";
import SectionColumnSpacingHandle from "../sections/SectionColumnSpacingHandle";
import { VariantContextData } from "../variants/VariantBuilderContext";
import arePropsEqual from "../../utils/arePropsEqual";
import { buildSectionColumnStylesheet } from "./SectionColumnStyleResolver";

const SectionColumn = (props) => {
  let section = props.section;
  let options = section.options["structure"];
  let column = props.column;
  const attributes = style.attributes(section, column);
  const stylesheet = buildSectionColumnStylesheet(
    props.builder,
    props.device,
    props.cta,
    props.variant,
    section,
    column
  );
  let contextData;

  if (props.builder == true) {
    [contextData] = useContext(VariantContextData);
  }

  const showBuilderColumnSpacingHandles =
    props.builder == true &&
    contextData.selected_object_type == "sections" &&
    contextData.selected_object_id == section.id &&
    contextData.selected_object_collapse == column.name;

  const sorted_elements = Object.values(section.elements)
    .filter(
      (e) =>
        e.section_id == section.id &&
        e.column == column.position &&
        e.toBeDeleted !== true
    )
    .sort((a, b) => a.position - b.position);

  if (props.builder == true) {
    useEffect(() => {
      sortElements.start(section, column);
    }, [section, column.name]);
  }

  return (
    <React.Fragment>
      <div
        column-position={column.name}
        data-column-position={column.name}
        section-id={section.id}
        data-section-id={section.id}
        data-column={options["cf-column-" + column.name]}
        data-column-layout={attributes.layout}
        data-column-layout-mobile={attributes.layout_mobile}
        data-mobile-responsive={
          options["mobile-responsive"] ? options["mobile-responsive"] : "true"
        }
        data-object-type="sections"
        data-object-id={section.id}
        className={
          attributes.classes +
          (props.builder == true &&
          window.selected_object_type == "sections" &&
          window.selected_object_id == section.id &&
          window.selected_object_collapse == column.name
            ? " editor-select"
            : "")
        }
      >
        <div
          section-id={section.id}
          data-section-id={section.id}
          className="cf-background-overlay"
          data-element-id={section.element_id}
        ></div>
        <div
          className="cf-cta-item-container"
          section-id={section.id}
          data-section-id={section.id}
          data-sortable-id={column.position}
          data-section-column={column.position}
        >
          <div className="cf-cta-item disabled-item"></div>
          {props.builder == true && (
            <p
              className="cf-draggable-creation-hover empty-container"
              data-object-type="Elements"
            >
              Column: Drag elements here
            </p>
          )}
          {sorted_elements.map((element, i) => {
            return (
              <Element
                key={"element-" + element.id}
                builder={props.builder}
                device={props.device}
                website={props.website}
                cta={props.cta}
                variant={props.variant}
                section={props.section}
                element={element}
                preview={props.preview}
              ></Element>
            );
          })}
        </div>
        {props.builder == true && (
          <div
            className="section-add-element"
            data-section-id={section.id}
            data-test-id={"section-add-element-" + section.id}
            style={{ display: "none" }}
            onClick={() => {
              dispatchCustomEvent("newObject", {
                object_type: "Elements",
                section_id: section.id,
                column: column.position,
                element_type: null,
              });
            }}
          >
            <span
              className="glyphicon glyphicon-plus"
              aria-hidden="true"
              data-section-id={section.id}
            ></span>
          </div>
        )}
        <style dangerouslySetInnerHTML={{ __html: stylesheet }} />
        {showBuilderColumnSpacingHandles && (
          <React.Fragment>
            <SectionColumnSpacingHandle
              key={`section-${section.id}-${column.name}-spacer-top`}
              {...props}
              direction="top"
              column={column.name}
            ></SectionColumnSpacingHandle>
            <SectionColumnSpacingHandle
              key={`section-${section.id}-${column.name}-spacer-bottom`}
              {...props}
              direction="bottom"
              column={column.name}
            ></SectionColumnSpacingHandle>
            <SectionColumnSpacingHandle
              key={`section-${section.id}-${column.name}-spacer-left`}
              {...props}
              direction="left"
              column={column.name}
            ></SectionColumnSpacingHandle>
            <SectionColumnSpacingHandle
              key={`section-${section.id}-${column.name}-spacer-right`}
              {...props}
              direction="right"
              column={column.name}
            ></SectionColumnSpacingHandle>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

const style = {
  predict_column_layout: (options) => {
    var response = "100";
    if (options["cf-column-first"] == "cf-col-md-12") response = "100";
    if (
      options["cf-column-first"] == "cf-col-md-6" &&
      options["cf-column-second"] == "cf-col-md-6"
    )
      response = "50-50";
    if (
      options["cf-column-first"] == "cf-col-md-7" &&
      options["cf-column-second"] == "cf-col-md-5"
    )
      response = "60-40";
    if (
      options["cf-column-first"] == "cf-col-md-5" &&
      options["cf-column-second"] == "cf-col-md-7"
    )
      response = "40-60";
    if (
      options["cf-column-first"] == "cf-col-md-8" &&
      options["cf-column-second"] == "cf-col-md-4"
    )
      response = "70-30";
    if (
      options["cf-column-first"] == "cf-col-md-4" &&
      options["cf-column-second"] == "cf-col-md-8"
    )
      response = "30-70";
    if (
      options["cf-column-first"] == "cf-col-md-9" &&
      options["cf-column-second"] == "cf-col-md-3"
    )
      response = "80-20";
    if (
      options["cf-column-first"] == "cf-col-md-3" &&
      options["cf-column-second"] == "cf-col-md-9"
    )
      response = "20-80";
    if (
      options["cf-column-first"] == "cf-col-md-4" &&
      options["cf-column-second"] == "cf-col-md-4" &&
      options["cf-column-third"] == "cf-col-md-4"
    )
      response = "33-33-33";
    if (
      options["cf-column-first"] == "cf-col-md-6" &&
      options["cf-column-second"] == "cf-col-md-3" &&
      options["cf-column-third"] == "cf-col-md-3"
    )
      response = "50-25-25";
    if (
      options["cf-column-first"] == "cf-col-md-3" &&
      options["cf-column-second"] == "cf-col-md-3" &&
      options["cf-column-third"] == "cf-col-md-6"
    )
      response = "25-25-50";
    if (
      options["cf-column-first"] == "cf-col-md-3" &&
      options["cf-column-second"] == "cf-col-md-3" &&
      options["cf-column-third"] == "cf-col-md-3" &&
      options["cf-column-fourth"] == "cf-col-md-3"
    )
      response = "25-25-25-25";
    return response;
  },
  attributes: (section, column) => {
    let options = section.options["structure"];

    let layout;
    if (options["column-layout"]) {
      layout = options["column-layout"];
    } else {
      layout = style.predict_column_layout(options);
    }

    let layout_mobile;
    if (options["column-layout-mobile"]) {
      layout_mobile = options["column-layout-mobile"];
    } else {
      layout_mobile = layout;
    }

    let classes =
      "cf-col cf-column cf-column-" +
      column.name +
      " " +
      options["cf-column-" + column.name];

    // set mobile column width
    if (options["cf-column-" + column.name + "-mobile"]) {
      classes += " " + options["cf-column-" + column.name + "-mobile"];
    } else if (options["cf-column-" + column.name]) {
      let mobile_width_fallback = options["cf-column-" + column.name].replace(
        "cf-col-md-",
        "cf-col-md-mobile-"
      );
      classes += " " + mobile_width_fallback;
    } else {
      classes += " " + "cf-col-md-mobile-12";
    }

    // desktop/mobile display options
    if (options[column.name + "-column-hide-desktop"] == "true") {
      classes += " " + "cf-hide-desktop";
    }
    if (options[column.name + "-column-hide-mobile"] == "true") {
      classes += " " + "cf-hide-mobile";
    }

    let background_image_position;
    if (options[`column-${column.name}-background-position`] !== "custom") {
      // background image classes
      background_image_position =
        "cf-background-position-" +
        (options["column-" + column.name + "-background-position"]
          ? options["column-" + column.name + "-background-position"]
          : "top-center");
    }

    let background_image_size;
    if (options[`column-${column.name}-background-size`] !== "custom") {
      background_image_size =
        "cf-background-size-" +
        (options["column-" + column.name + "-background-size"]
          ? options["column-" + column.name + "-background-size"]
          : "cover");
    }

    classes += " " + background_image_position;
    classes += " " + background_image_size;

    if (options["cf-column-" + column.name + "-custom-class"]) {
      classes += " " + options["cf-column-" + column.name + "-custom-class"];
    }

    return {
      classes: classes,
      layout: layout,
      layout_mobile: layout_mobile,
    };
  },
};

export default React.memo(SectionColumn, arePropsEqual);
